import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface PreCallPlanPDFData {
    id: number;
    pre_call_plan_id?: number; // Add this field to fix linter errors
    salesman_username?: string | null;
    product_description?: string | null;
    customer_fullname?: string | null;
    key_message?: string | null;
    buying_indicator?: string | null;
    collection?: string | null;
    competition_presence?: string | null;
    contact_feedback?: string | null;
    remarks?: string | null; // Add this field to fix linter errors
    planned_date?: string | null; // Date when the call is planned
    created_time: string;
    precallplanproducts?: Array<{
        id?: number;
        pre_call_plan_id: number;
        product_id: number;
        description?: string | null;
        remarks?: string | null;
        created_time?: string;
        updated_time?: string;
    }> | null;
}

interface PostCallPlanPDFData {
    id: number;
    pre_call_plan_id: number;
    salesman_username?: string | null;
    product_description?: string | null;
    customer_fullname?: string | null;
    remarks?: string | null;
    created_time: string;
    planned_date?: string | null; // Planned date from the connected pre-call plan
}

export const generatePreCallPlanPDF = (plan: PreCallPlanPDFData) => {
    const doc = new jsPDF();

    // Add company logo/header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Pre-Call Plan Report', 105, 30, { align: 'center' });

    // Add report details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Report Time: ${new Date().toLocaleTimeString()}`, 20, 50);
    doc.text(`Plan ID: ${plan.id}`, 20, 55);

    // Add plan details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Plan Details', 20, 70);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const details = [
        ['Salesman', plan.salesman_username || 'N/A'],
        ['Customer', plan.customer_fullname || 'N/A'],
        ['Planned Date', plan.planned_date ? new Date(plan.planned_date).toLocaleDateString() : 'Not set'],
        ['Created Date', new Date(plan.created_time).toLocaleDateString()],
        ['Key Message', plan.key_message || 'N/A'],
        ['Contact Feedback', plan.contact_feedback || 'N/A'],
        ['Competition Presence', plan.competition_presence || 'N/A'],
        ['Buying Indicator', plan.buying_indicator || 'N/A'],
        ['Collection', plan.collection || 'N/A']
    ];

    // Use autoTable for better formatting
    (doc as any).autoTable({
        startY: 75,
        head: [['Field', 'Value']],
        body: details,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 9 },
        columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 50 },
            1: { cellWidth: 120 }
        }
    });

    // Add products section if available
    if (plan.precallplanproducts && plan.precallplanproducts.length > 0) {
        const currentY = (doc as any).lastAutoTable.finalY + 10;

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text(`Products (${plan.precallplanproducts.length})`, 20, currentY);

        const productDetails = plan.precallplanproducts.map((product, index) => [
            index + 1,
            product.product_id,
            product.description || 'No description',
            product.remarks || 'No remarks',
            product.created_time ? new Date(product.created_time).toLocaleDateString() : 'N/A'
        ]);

        (doc as any).autoTable({
            startY: currentY + 5,
            head: [['#', 'Product ID', 'Description', 'Remarks', 'Created Date']],
            body: productDetails,
            theme: 'grid',
            headStyles: { fillColor: [46, 204, 113], textColor: 255 },
            styles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 15 },
                1: { cellWidth: 25 },
                2: { cellWidth: 50 },
                3: { cellWidth: 40 },
                4: { cellWidth: 30 }
            }
        });
    } else {
        // No products message
        const currentY = (doc as any).lastAutoTable.finalY + 10;
        doc.setFontSize(10);
        doc.setFont('helvetica', 'italic');
        doc.text('No products associated with this plan', 20, currentY);
    }

    // Add footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('Generated by Global Pharma Warehouse Management System', 105, pageHeight - 10, { align: 'center' });

    return doc;
};

export const generateMultiplePreCallPlansPDF = (plans: PreCallPlanPDFData[], title: string) => {
    const doc = new jsPDF();

    // Add company logo/header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 105, 30, { align: 'center' });

    // Add report details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Report Time: ${new Date().toLocaleTimeString()}`, 20, 50);
    doc.text(`Total Plans: ${plans.length}`, 20, 55);

    if (plans.length === 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('No plans found for the selected period.', 20, 75);

        // Add footer
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text('Generated by Global Pharma Warehouse Management System', 105, pageHeight - 10, { align: 'center' });

        return doc;
    }

    // Create summary table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Plans Summary', 20, 70);

    const summaryData = plans.map((plan, index) => [
        index + 1,
        plan.salesman_username || 'N/A',
        plan.customer_fullname || 'N/A',
        plan.planned_date ? new Date(plan.planned_date).toLocaleDateString() : new Date(plan.created_time).toLocaleDateString(),
        plan.precallplanproducts?.length || 0
    ]);

    (doc as any).autoTable({
        startY: 75,
        head: [['#', 'Salesman', 'Customer', 'Planned Date', 'Products']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 8 },
        columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 45 },
            2: { cellWidth: 45 },
            3: { cellWidth: 35 },
            4: { cellWidth: 25 }
        }
    });

    // Add detailed plans with all information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Detailed Plans', 20, (doc as any).lastAutoTable.finalY + 15);

    plans.forEach((plan, index) => {
        let startY = (doc as any).lastAutoTable.finalY + 20;

        // Check if we need a new page
        if (startY > doc.internal.pageSize.height - 100) {
            doc.addPage();
            (doc as any).lastAutoTable.finalY = 20;
            startY = 25;
        }

        // Plan header
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`Plan ${index + 1} - ID: ${plan.id}`, 20, startY);

        // Basic plan details
        const planDetails = [
            ['Salesman', plan.salesman_username || 'N/A'],
            ['Customer', plan.customer_fullname || 'N/A'],
            ['Planned Date', plan.planned_date ? new Date(plan.planned_date).toLocaleDateString() : 'Not set'],
            ['Pre-Call Plan ID', plan.pre_call_plan_id?.toString() || 'N/A'],
            ['Created Date', new Date(plan.created_time).toLocaleDateString()],
            ['Product', plan.product_description || 'N/A'],
            ['Remarks', plan.remarks || 'N/A']
        ];

        (doc as any).autoTable({
            startY: startY + 5,
            head: [['Field', 'Value']],
            body: planDetails,
            theme: 'grid',
            headStyles: { fillColor: [52, 152, 219], textColor: 255 },
            styles: { fontSize: 8 },
            columnStyles: {
                0: { fontStyle: 'bold', cellWidth: 45 },
                1: { cellWidth: 125 }
            }
        });

        // Add products section if available
        if (plan.precallplanproducts && plan.precallplanproducts.length > 0) {
            const productsStartY = (doc as any).lastAutoTable.finalY + 10;

            // Check if we need a new page for products
            if (productsStartY > doc.internal.pageSize.height - 60) {
                doc.addPage();
                (doc as any).lastAutoTable.finalY = 20;
            }

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Products (${plan.precallplanproducts.length})`, 20, (doc as any).lastAutoTable.finalY + 10);

            const productDetails = plan.precallplanproducts.map((product, productIndex) => [
                productIndex + 1,
                product.product_id,
                product.description || 'No description',
                product.remarks || 'No remarks',
                product.created_time ? new Date(product.created_time).toLocaleDateString() : 'N/A'
            ]);

            (doc as any).autoTable({
                startY: (doc as any).lastAutoTable.finalY + 5,
                head: [['#', 'Product ID', 'Description', 'Remarks', 'Created Date']],
                body: productDetails,
                theme: 'grid',
                headStyles: { fillColor: [46, 204, 113], textColor: 255 },
                styles: { fontSize: 7 },
                columnStyles: {
                    0: { cellWidth: 15 },
                    1: { cellWidth: 25 },
                    2: { cellWidth: 50 },
                    3: { cellWidth: 40 },
                    4: { cellWidth: 30 }
                }
            });
        } else {
            // No products message
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.text('No products associated with this plan', 20, (doc as any).lastAutoTable.finalY + 10);
        }

        // Add separator between plans
        if (index < plans.length - 1) {
            doc.setDrawColor(200, 200, 200);
            doc.line(20, (doc as any).lastAutoTable.finalY + 15, 190, (doc as any).lastAutoTable.finalY + 15);
            doc.setDrawColor(0, 0, 0);
        }
    });

    // Add footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('Generated by Global Pharma Warehouse Management System', 105, pageHeight - 10, { align: 'center' });

    return doc;
};

export const generatePostCallPlanPDF = (plan: PostCallPlanPDFData) => {
    const doc = new jsPDF();

    // Add company logo/header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Post-Call Plan Report', 105, 30, { align: 'center' });

    // Add report details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Report Time: ${new Date().toLocaleTimeString()}`, 20, 50);
    doc.text(`Plan ID: ${plan.id}`, 20, 55);
    doc.text(`Pre-Call Plan ID: ${plan.pre_call_plan_id}`, 20, 60);

    // Add plan details
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Plan Details', 20, 75);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');

    const details = [
        ['Salesman', plan.salesman_username || 'N/A'],
        ['Customer', plan.customer_fullname || 'N/A'],
        ['Planned Date', plan.planned_date ? new Date(plan.planned_date).toLocaleDateString() : 'Not set'],
        ['Pre-Call Plan ID', plan.pre_call_plan_id.toString()],
        ['Created Date', new Date(plan.created_time).toLocaleDateString()],
        ['Product', plan.product_description || 'N/A'],
        ['Remarks', plan.remarks || 'N/A']
    ];

    // Use autoTable for better formatting
    (doc as any).autoTable({
        startY: 80,
        head: [['Field', 'Value']],
        body: details,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 9 },
        columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 50 },
            1: { cellWidth: 120 }
        }
    });

    // Add footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('Generated by Global Pharma Warehouse Management System', 105, pageHeight - 10, { align: 'center' });

    return doc;
};

export const generateMultiplePostCallPlansPDF = (plans: PostCallPlanPDFData[], title: string) => {
    const doc = new jsPDF();

    // Add company logo/header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');

    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 105, 30, { align: 'center' });

    // Add report details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Report Date: ${new Date().toLocaleDateString()}`, 20, 45);
    doc.text(`Report Time: ${new Date().toLocaleTimeString()}`, 20, 50);
    doc.text(`Total Plans: ${plans.length}`, 20, 55);

    if (plans.length === 0) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('No plans found for the selected period.', 20, 75);

        // Add footer
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text('Generated by Global Pharma Warehouse Management System', 105, pageHeight - 10, { align: 'center' });

        return doc;
    }

    // Create summary table
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Plans Summary', 20, 70);

    const summaryData = plans.map((plan, index) => [
        index + 1,
        plan.salesman_username || 'N/A',
        plan.customer_fullname || 'N/A',
        plan.planned_date ? new Date(plan.planned_date).toLocaleDateString() : 'Not set',
        new Date(plan.created_time).toLocaleDateString(),
        plan.pre_call_plan_id
    ]);

    (doc as any).autoTable({
        startY: 75,
        head: [['#', 'Salesman', 'Customer', 'Planned Date', 'Created Date', 'Pre-Call Plan ID']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 8 },
        columnStyles: {
            0: { cellWidth: 15 },
            1: { cellWidth: 40 },
            2: { cellWidth: 40 },
            3: { cellWidth: 30 },
            4: { cellWidth: 30 },
            5: { cellWidth: 30 }
        }
    });

    // Add detailed plans with all information
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Detailed Plans', 20, (doc as any).lastAutoTable.finalY + 15);

    plans.forEach((plan, index) => {
        let startY = (doc as any).lastAutoTable.finalY + 20;

        // Check if we need a new page
        if (startY > doc.internal.pageSize.height - 100) {
            doc.addPage();
            (doc as any).lastAutoTable.finalY = 20;
            startY = 25;
        }

        // Plan header
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`Plan ${index + 1} - ID: ${plan.id}`, 20, startY);

        // Basic plan details
        const planDetails = [
            ['Salesman', plan.salesman_username || 'N/A'],
            ['Customer', plan.customer_fullname || 'N/A'],
            ['Planned Date', plan.planned_date ? new Date(plan.planned_date).toLocaleDateString() : 'Not set'],
            ['Pre-Call Plan ID', plan.pre_call_plan_id.toString()],
            ['Created Date', new Date(plan.created_time).toLocaleDateString()],
            ['Product', plan.product_description || 'N/A'],
            ['Remarks', plan.remarks || 'N/A']
        ];

        (doc as any).autoTable({
            startY: startY + 5,
            head: [['Field', 'Value']],
            body: planDetails,
            theme: 'grid',
            headStyles: { fillColor: [52, 152, 219], textColor: 255 },
            styles: { fontSize: 8 },
            columnStyles: {
                0: { fontStyle: 'bold', cellWidth: 45 },
                1: { cellWidth: 125 }
            }
        });

        // Add separator between plans
        if (index < plans.length - 1) {
            doc.setDrawColor(200, 200, 200);
            doc.line(20, (doc as any).lastAutoTable.finalY + 15, 190, (doc as any).lastAutoTable.finalY + 15);
            doc.setDrawColor(0, 0, 0);
        }
    });

    // Add footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text('Generated by Global Pharma Warehouse Management System', 105, pageHeight - 10, { align: 'center' });

    return doc;
};

export const printPDF = (doc: jsPDF, filename: string) => {
    // Save the PDF
    doc.save(filename);

    // Also open in new window for printing
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl);

    if (printWindow) {
        printWindow.onload = () => {
            printWindow.print();
        };
    }
};
